import { useCallback, useEffect, useRef, useState } from 'react'
import { BASE_URL, getAccessToken } from '../../api/client'

/**
 * El chat de ayuda, por dentro.
 *
 * Habla con `/api/v1/support/…` de nuestro propio backend, que firma quién
 * pregunta y reenvía a support.dataanalisis.com. Aquí no hay ni secreto ni
 * dirección del servicio: sólo la sesión que ya tenemos.
 *
 * La respuesta llega en streaming (SSE) y se pinta según sale. Cuando pasa a una
 * persona, el asistente calla y esto sondea lo nuevo cada pocos segundos
 * mientras el panel siga abierto — es como aparece la respuesta del humano en el
 * mismo hilo.
 *
 * `fetch` y no `EventSource` a propósito: el chat es un POST con cuerpo y con la
 * cabecera de sesión, y `EventSource` sólo sabe hacer GET sin cabeceras.
 */

const CLAVE = 'support:conversation'
/* 4 s y no 7: con 7, «Rosa está escribiendo…» llegaba cuando Rosa ya había terminado. */
const SONDEO_MS = 4000
/* Avisar de que escribes como mucho una vez cada tanto: basta para que el aviso no se apague. */
const AVISO_ESCRIBIENDO_MS = 3000

export type Rol = 'user' | 'assistant' | 'human'
/** `autor`: el nombre de pila de la persona del equipo que escribió, solo en los de `human`. */
export type Mensaje = { seq?: number; rol: Rol; texto: string; autor?: string | null; pendiente?: boolean }
export type Estado = 'bot' | 'pending_human' | 'resolved'

type EventoSse = { evento: string; datos: Record<string, unknown> }

/** Lee un cuerpo SSE y entrega evento a evento. El formato es `event: x\ndata: {…}\n\n`. */
async function* leerSse(body: ReadableStream<Uint8Array>): AsyncGenerator<EventoSse> {
  const lector = body.getReader()
  const decodificador = new TextDecoder()
  let resto = ''
  for (;;) {
    const { value, done } = await lector.read()
    if (done) break
    resto += decodificador.decode(value, { stream: true })
    let corte: number
    while ((corte = resto.indexOf('\n\n')) !== -1) {
      const bruto = resto.slice(0, corte)
      resto = resto.slice(corte + 2)
      let evento = 'message'
      let datos = ''
      for (const linea of bruto.split('\n')) {
        if (linea.startsWith('event: ')) evento = linea.slice(7)
        else if (linea.startsWith('data: ')) datos += linea.slice(6)
      }
      if (datos !== '') yield { evento, datos: JSON.parse(datos) as Record<string, unknown> }
    }
  }
}

function cabeceras(): HeadersInit {
  const token = getAccessToken()
  return {
    'content-type': 'application/json',
    ...(token === null ? {} : { authorization: `Bearer ${token}` }),
  }
}

export function useSupportChat(abierto: boolean) {
  const [mensajes, setMensajes] = useState<Mensaje[]>([])
  const [estado, setEstado] = useState<Estado>('bot')
  const [conversacion, setConversacion] = useState<string | null>(null)
  const [referencia, setReferencia] = useState<string | null>(null)
  const [whatsapp, setWhatsapp] = useState<string | null>(null)
  const [ultimoSeq, setUltimoSeq] = useState(0)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* `null` = todavía no se sabe; entonces la nota dice lo de siempre, sin prometer ni negar. */
  const [equipoConectado, setEquipoConectado] = useState<boolean | null>(null)
  const [escribiendo, setEscribiendo] = useState<{ name: string | null } | null>(null)
  const ultimoAviso = useRef(0)
  /* En una ref además del estado: el sondeo se crea con el valor del render en
     que se montó, y sin la ref pediría siempre desde el mismo mensaje. */
  const seqRef = useRef(0)

  const recordar = useCallback((id: string) => {
    setConversacion(id)
    try {
      sessionStorage.setItem(CLAVE, id)
    } catch {
      /* Sin almacenamiento (modo privado): el hilo dura lo que dure la pantalla. */
    }
  }, [])

  /* Retomar el hilo al abrir. */
  useEffect(() => {
    if (!abierto || conversacion !== null) return
    let guardada: string | null = null
    try {
      guardada = sessionStorage.getItem(CLAVE)
    } catch {
      guardada = null
    }
    if (guardada === null) return
    const id = guardada
    void fetch(`${BASE_URL}/support/conversations/${id}?after=0`, { headers: cabeceras() })
      .then((r) => (r.ok ? (r.json() as Promise<{ status: Estado; referenceCode: string; messages: { seq: number; role: Rol; content: string; author?: string | null }[]; team?: { online: boolean } }>) : null))
      .then((d) => {
        if (d === null) return
        setConversacion(id)
        setEstado(d.status)
        if (d.team) setEquipoConectado(d.team.online)
        setReferencia(d.referenceCode)
        setMensajes(d.messages.map((m) => ({ seq: m.seq, rol: m.role, texto: m.content, autor: m.author })))
        const ultimo = d.messages.at(-1)?.seq ?? 0
        setUltimoSeq(ultimo)
        seqRef.current = ultimo
      })
      .catch(() => {
        /* Si no se puede retomar, se empieza limpio: no es un error que contar. */
      })
  }, [abierto, conversacion])

  /* Mientras se espera a una persona, traer lo nuevo. */
  useEffect(() => {
    if (!abierto || conversacion === null || estado !== 'pending_human') return
    const t = setInterval(() => {
      void fetch(`${BASE_URL}/support/conversations/${conversacion}?after=${seqRef.current}`, { headers: cabeceras() })
        .then((r) => (r.ok ? (r.json() as Promise<{ status: Estado; messages: { seq: number; role: Rol; content: string; author?: string | null }[]; team?: { online: boolean }; typing?: { name: string | null } | null }>) : null))
        .then((d) => {
          if (d === null) return
          if (d.messages.length > 0) {
            setMensajes((m) => [...m, ...d.messages.map((x) => ({ seq: x.seq, rol: x.role, texto: x.content, autor: x.author }))])
            const ultimo = d.messages.at(-1)?.seq ?? seqRef.current
            seqRef.current = ultimo
            setUltimoSeq(ultimo)
          }
          setEstado(d.status)
          if (d.team) setEquipoConectado(d.team.online)
          setEscribiendo(d.typing ?? null)
        })
        .catch(() => {
          /* El siguiente tick lo reintenta. */
        })
    }, SONDEO_MS)
    return () => clearInterval(t)
  }, [abierto, conversacion, estado])

  const enviar = useCallback(
    async (texto: string) => {
      const contenido = texto.trim()
      if (contenido === '' || enviando) return
      setError(null)
      setEnviando(true)
      setMensajes((m) => [...m, { rol: 'user', texto: contenido }, { rol: 'assistant', texto: '', pendiente: true }])
      try {
        const res = await fetch(`${BASE_URL}/support/chat`, {
          method: 'POST',
          headers: cabeceras(),
          body: JSON.stringify({ conversationId: conversacion ?? undefined, message: contenido }),
        })
        if (!res.ok || res.body === null) {
          /* El sobre de error de la API es `{ error: { code, message } }`, y `new Error(objeto)`
             pintaba «[object Object]». De un 5xx, además, al cliente no le sirve el detalle. */
          const d = (await res.json().catch(() => null)) as { error?: string | { message?: string } } | null
          const detalle = typeof d?.error === 'string' ? d.error : d?.error?.message
          throw new Error(res.status < 500 && detalle ? detalle : 'No se pudo enviar. Vuelve a intentarlo.')
        }
        for await (const { evento, datos } of leerSse(res.body)) {
          if (evento === 'meta') {
            recordar(datos.conversationId as string)
            setReferencia(datos.referenceCode as string)
          } else if (evento === 'text') {
            const trozo = datos.text as string
            setMensajes((m) => {
              const ultimo = m[m.length - 1]
              if (ultimo === undefined || ultimo.rol !== 'assistant') return m
              return [...m.slice(0, -1), { ...ultimo, texto: ultimo.texto + trozo }]
            })
          } else if (evento === 'done') {
            setEstado(datos.status as Estado)
            const seq = datos.lastSeq as number | undefined
            if (seq !== undefined) {
              seqRef.current = seq
              setUltimoSeq(seq)
            }
            if (typeof datos.whatsapp === 'string') setWhatsapp(datos.whatsapp)
            if (typeof datos.teamOnline === 'boolean') setEquipoConectado(datos.teamOnline)
          } else if (evento === 'error') {
            setError(datos.message as string)
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo enviar. Vuelve a intentarlo.')
      } finally {
        /* La burbuja vacía del asistente no se queda: o llegó texto, o hubo error. */
        setMensajes((m) => m.filter((x) => !(x.rol === 'assistant' && x.texto === '')).map((x) => ({ ...x, pendiente: false })))
        setEnviando(false)
      }
    },
    [conversacion, enviando, recordar],
  )

  const pedirPersona = useCallback(async () => {
    if (conversacion === null || enviando) return
    setEnviando(true)
    try {
      const r = await fetch(`${BASE_URL}/support/conversations/${conversacion}/escalate`, {
        method: 'POST',
        headers: cabeceras(),
      })
      if (r.ok) {
        const d = (await r.json()) as { status: Estado; referenceCode: string; whatsapp?: string; teamOnline?: boolean }
        setEstado(d.status)
        setReferencia(d.referenceCode)
        if (typeof d.whatsapp === 'string') setWhatsapp(d.whatsapp)
        if (typeof d.teamOnline === 'boolean') setEquipoConectado(d.teamOnline)
      }
    } finally {
      setEnviando(false)
    }
  }, [conversacion, enviando])

  /* El estudio escribe a una persona: se le avisa a la bandeja, como mucho cada 3 s. Solo que escribe, nunca qué. */
  const avisarEscribiendo = useCallback(() => {
    if (conversacion === null || estado !== 'pending_human') return
    const ahora = Date.now()
    if (ahora - ultimoAviso.current < AVISO_ESCRIBIENDO_MS) return
    ultimoAviso.current = ahora
    void fetch(`${BASE_URL}/support/conversations/${conversacion}/typing`, { method: 'POST', headers: cabeceras() }).catch(() => {})
  }, [conversacion, estado])

  const reiniciar = useCallback(() => {
    setEquipoConectado(null)
    setEscribiendo(null)
    setMensajes([])
    setConversacion(null)
    setReferencia(null)
    setEstado('bot')
    setUltimoSeq(0)
    seqRef.current = 0
    setError(null)
    try {
      sessionStorage.removeItem(CLAVE)
    } catch {
      /* Nada que borrar. */
    }
  }, [])

  return {
    mensajes, estado, conversacion, referencia, whatsapp, ultimoSeq, enviando, error,
    equipoConectado, escribiendo, enviar, pedirPersona, avisarEscribiendo, reiniciar,
  }
}
